import type { PageHint } from '../types';

/**
 * Where the applicant assistant is, and what it suggests there. Focus screens
 * are timed, scored, on camera, or a full-screen editor: it stays out of them
 * entirely. Not `isDoNotDisturbPath`: that list also silences the store, where
 * the assistant is wanted.
 */
export const FOCUS_SCREENS = [
  /\/play(\/|$)/, // competition, skill, course, skill-test and multiplayer play
  /^\/skill-builder\/multiplayer\/[^/]+/, // a room or the arena
  /^\/interview\/[^/]+\/join/,
  /^\/course\/[^/]+\/test\//,
  /^\/course\/[^/]+\/read\//, // a lesson has its own AI Guide beside the cards
  /^\/course\/[^/]+\/intro\//, // the orientation deck: the orb sat over its Next button
  /^\/classes\/[^/]+\/quizzes\//, // a class quiz attempt: timed, graded by the teacher
  /^\/evaluation-assessment\//,
  /\/assessment(\/|$)/,
  /^\/onboarding/,
  /^\/candidate-post/,
  /^\/resume-builder\/(new|job-fit-preview|job-fit-app-preview|template-shot)/,
  /^\/cover-letter\/print/,
  /^\/coding-practice\/(?!category\/)[^/]+/, // the code editor, not the lists
];

/** Pages with a bar pinned to the bottom on small screens. */
export const BOTTOM_BAR_PAGES = [
  /^\/competition\/[^/]+\/?$/,
  /^\/job-search\/[^/]+\/?$/,
  /^\/applied-jobs\/[^/]+\/?$/,
  /^\/chat\/candidate/,
];

export const PAGE_HINTS: PageHint[] = [
  {
    pattern: /^\/competition\/[^/]+/,
    label: 'This competition',
    suggestions: [
      'When does this competition end?',
      'What can I win here?',
      'Register me for this',
    ],
  },
  {
    pattern: /^\/competition\/?$/,
    label: 'Competitions',
    suggestions: [
      "What's live right now?",
      'Which competitions am I registered for?',
      'How did I rank last time?',
    ],
  },
  {
    pattern: /^\/(job-search|applied-jobs)\/[^/]+/,
    label: 'This job',
    suggestions: [
      'Is this job a good fit for me?',
      'What does this role pay?',
      'Apply for me',
    ],
  },
  {
    pattern: /^\/job-search\/?$/,
    label: 'Find Jobs',
    suggestions: [
      'Show remote jobs in Bengaluru',
      'Only internships posted this week',
      'Show my saved jobs',
    ],
  },
  {
    pattern: /^\/applied-jobs\/?$/,
    label: 'Applied Jobs',
    suggestions: [
      'Where do my applications stand?',
      'Any shortlists or interviews?',
      'Find more jobs like these',
    ],
  },
  {
    pattern: /^\/course\/[^/]+/,
    label: 'This course',
    suggestions: [
      'How far am I in this course?',
      'Do I get a certificate?',
      'What should I learn next?',
    ],
  },
  {
    pattern: /^\/course\/?$/,
    label: 'Courses',
    suggestions: [
      'Show Python courses',
      'Show learning paths',
      'Create my own course',
    ],
  },
  {
    pattern: /^\/skill-builder\/[^/]+/,
    label: 'This skill',
    suggestions: [
      'How am I doing in this skill?',
      'What are my weak spots here?',
      'Challenge my friends on this',
    ],
  },
  {
    pattern: /^\/skill-builder\/?$/,
    label: 'Skill Builder',
    suggestions: [
      'What should I practise next?',
      'Where do I rank in SQL?',
      'Join a quiz room with a code',
    ],
  },
  {
    pattern: /^\/coding-practice/,
    label: 'Coding Practice',
    suggestions: [
      'How many problems have I solved?',
      'Find easy array problems',
      'Which badge is next?',
    ],
  },
  {
    pattern: /^\/my-interviews/,
    label: 'My Interviews',
    suggestions: [
      'When is my next interview?',
      'Accept my interview invite',
      'How do I prepare for it?',
    ],
  },
  {
    pattern: /^\/chat\/candidate/,
    label: 'Messages',
    suggestions: [
      'Any unread recruiter messages?',
      'Ask the recruiter for an update',
      'Which applications have replies?',
    ],
  },
  {
    pattern: /^\/rewards/,
    label: 'Rewards',
    suggestions: [
      "Claim today's missions",
      'What does the next level pay?',
      'Which badge can I get next?',
    ],
  },
  {
    pattern: /^\/user\/referral/,
    label: 'Refer & Earn',
    suggestions: [
      'How many friends have I referred?',
      'What do I earn per referral?',
      'How many credits have I earned?',
    ],
  },
  {
    pattern: /^\/leaderboard/,
    label: 'Leaderboard',
    suggestions: [
      'Where do I rank this week?',
      'Who is top in SQL?',
      'How do I climb the leaderboard?',
    ],
  },
  {
    pattern: /^\/classes/,
    label: 'Classes',
    suggestions: [
      'Which class quizzes are due?',
      'Did I miss any quizzes?',
      'How did I score last time?',
    ],
  },
  {
    pattern: /^\/skill-test/,
    label: 'Skill Tests',
    suggestions: [
      'Which tests can I take?',
      'Show Python skill tests',
      'Do I have any certificates?',
    ],
  },
  {
    pattern: /^\/career-guidance/,
    label: 'Career Guidance',
    suggestions: [
      'Can I book a call?',
      'What does a call cost me?',
      'When is my next call?',
    ],
  },
  {
    pattern: /^\/applicant-dashboard/,
    label: 'Dashboard',
    suggestions: [
      'What should I do today?',
      'Any interviews or recruiter messages?',
      'Find jobs that match me',
    ],
  },
  {
    pattern: /^\/support/,
    label: 'Support',
    suggestions: [
      'I have a problem I need help with',
      'Check my support tickets',
      'Report a bug',
    ],
  },
  {
    pattern: /^\/store/,
    label: 'Store',
    suggestions: [
      'What do I get with Pro?',
      'How much does Pro cost?',
      'What is Spotlight+?',
    ],
  },
  {
    pattern: /^\/profile/,
    label: 'Your profile',
    suggestions: [
      "What's missing from my profile?",
      'How do I stand out to recruiters?',
      'Open the resume builder',
    ],
  },
  {
    pattern: /^\/resume-builder/,
    label: 'Resume Builder',
    suggestions: [
      'How do I make my resume ATS-friendly?',
      'Which templates are free?',
      'How do I tailor my resume to a job?',
    ],
  },
];

export const GENERAL_SUGGESTIONS = [
  'Find jobs that match me',
  "What's live in competitions?",
  'How many credits do I have?',
  'I need help from support',
];

/** Ways into a feature, each skipped on its own pages. */
export const EXPLORE_SUGGESTIONS: { pattern: RegExp; text: string }[] = [
  { pattern: /^\/competition/, text: 'Any hiring challenge live now?' },
  { pattern: /^\/profile/, text: "What's missing from my profile?" },
  { pattern: /^\/rewards/, text: "Claim today's missions" },
  { pattern: /^\/job-search/, text: 'Find jobs that match me' },
  { pattern: /^\/course/, text: 'Suggest a course for my goal' },
  { pattern: /^\/applied-jobs/, text: 'Where do my applications stand?' },
  { pattern: /^\/skill-builder/, text: 'Quiz my friends live' },
  { pattern: /^\/resume-builder/, text: 'Make my resume ATS-friendly' },
  { pattern: /^\/competition/, text: 'What can I win this week?' },
];

/**
 * Cycled through the empty composer: things it does, not just answers. Kept
 * short: the input sizes itself to its placeholder, so a hint that wraps on a
 * phone would make it jump a line taller.
 */
export const TRY_SAYING = [
  'Find remote React jobs',
  "Claim today's missions",
  'Track my applications',
  'Suggest a SQL course',
  'Turn on auto-apply',
  'What can I win today?',
];
